/**
 * Data model — the single source of truth.
 *
 * This layer knows NOTHING about how things are drawn. Coordinates are always
 * in WORLD space; the camera maps world <-> screen in the render layer.
 */

/**
 * A card is a free `text` node (su texto plano vive en `text`, dentro del
 * .canvas), a `file` node that embeds a markdown document from the vault (el
 * canvas guarda la ruta, no el contenido), or an `ink` node holding one
 * freehand pencil stroke. For file nodes `markdown` is just an in-memory cache
 * of the referenced file's contents — it is never persisted into the .canvas.
 */
export type CardType = "text" | "file" | "ink" | "group" | "shape" | "comment";

/**
 * Las formas que ofrece la herramienta, en el orden del selector.
 *
 * Son cuatro a propósito, y son las cuatro de un diagrama: proceso, inicio/fin,
 * decisión y terminador. `roundRect` no está porque un radio es una propiedad y
 * no una forma —`rect` ya se dibuja con uno—, y el cuadrado y el círculo salen
 * de dibujar con Shift, que fija la proporción 1:1.
 */
export type ShapeKind = "rect" | "ellipse" | "diamond" | "pill";

/** Relleno de una forma nueva cuando no hay ninguno elegido. */
export const DEFAULT_SHAPE_FILL = "#343331";

/**
 * Una forma geométrica con texto adentro: la caja de un diagrama.
 *
 * El texto vive ACÁ y no en `Card.markdown`. Antes compartía ese campo con las
 * tarjetas y de ahí venía media docena de defectos: se rendereaba como markdown
 * (una viñeta adentro de un rombo), pasaba por el editor y el caché de textura
 * de las tarjetas, y saltaba de lugar al confirmar porque el editor y la
 * textura lo maquetaban distinto. Con el texto en su propio campo, nada de lo
 * que lee `markdown` puede tocar una forma: la separación deja de depender de
 * acordarse de escribir el `if`. Ver `docs/plan-formas.md`.
 *
 * El relleno es SÓLIDO y sin borde: el color que se elige es el color que se
 * ve. El color del texto no se guarda, se calcula por contraste contra `fill`
 * (ver `render/shapeStyle.ts`).
 */
export interface ShapeData {
  kind: ShapeKind;
  /** Relleno CSS hex, sólido y sin transparencia. */
  fill: string;
  /** El texto de la caja. Texto plano: no es markdown. */
  text: string;
  /**
   * Alineación horizontal del texto adentro de la figura. Es la misma pregunta
   * —y la misma barra— que la de un nodo de texto: una etiqueta de una línea se
   * quiere centrada, pero una lista adentro de un rectángulo se lee pegada a la
   * izquierda.
   *
   * El vertical sigue siendo siempre centrado: adentro de una figura, el texto
   * flota en el medio (ver `valign` en `render/canvasText.ts`).
   */
  align: TextAlign;
}

/** Alineación horizontal del texto de un nodo `text`. */
export type TextAlign = "left" | "center" | "right";

/** Un nodo de texto sin fondo: el que no pinta nada detrás de las letras. */
export const TEXT_BG_NONE = "none";
/** El único fondo sólido que ofrece hoy la barra. */
export const TEXT_BG_WHITE = "#ffffff";

/**
 * El color con el que nace un nodo de texto, y el que tiene cualquiera escrito
 * antes de que el color se pudiera elegir.
 *
 * Vive acá y no en `render/textStyle.ts` por el mismo motivo que `TEXT_BG_WHITE`
 * y `DEFAULT_SHAPE_FILL`: desde que se guarda en el documento dejó de ser una
 * decisión de dibujo y pasó a ser un valor del modelo. La capa de render ya no
 * tiene un color de texto propio: usa el del nodo.
 */
export const TEXT_COLOR_DEFAULT = "#1a1a1f";

/**
 * Un nodo de texto suelto sobre el lienzo.
 *
 * Es el hermano de `ShapeData`, y no por casualidad: los dos son texto plano
 * dentro de una caja, y desde esta versión los dibuja y los edita LA MISMA capa
 * (`render/canvasText.ts`). Antes el texto suelto era markdown que se
 * rasterizaba a una textura y se editaba con CodeMirror, y de ahí venían sus
 * defectos: se veía borroso al acercar, saltaba de lugar al confirmar y
 * cualquier cambio obligaba a re-rasterizar la tarjeta entera.
 *
 * El texto vive ACÁ y no en `Card.markdown` por el mismo motivo que el de una
 * forma: mientras compartía ese campo, todo lo que lee `markdown` —el caché de
 * texturas, el pipeline de markdown, el editor de tarjetas— podía tocarlo.
 *
 * Lo que lo diferencia de una forma es lo que se ve: el fondo puede ser
 * invisible (`TEXT_BG_NONE`) y el texto se alinea a izquierda, centro o
 * derecha, en lugar de ir siempre centrado adentro de una figura.
 */
export interface TextData {
  /** El texto del nodo. Texto plano: no es markdown. */
  text: string;
  align: TextAlign;
  /** `TEXT_BG_NONE` o un hex sólido (hoy, `TEXT_BG_WHITE`). */
  background: string;
  /**
   * Color de las letras: un hex sólido. A diferencia del de una forma —que se
   * calcula por contraste contra el relleno— acá se guarda, porque el fondo de
   * un nodo de texto casi nunca existe y no hay nada contra qué contrastar:
   * quien escribe elige el tono.
   */
  color: string;
  /**
   * `true` = el ancho lo pide el texto (el nodo nació de un click y todavía
   * nadie lo estiró); `false` = el ancho es de quien lo redimensionó y el texto
   * se envuelve adentro.
   *
   * Es la distinción de Figma entre texto auto-ancho y texto de ancho fijo, y
   * hace falta guardarla: sin ella, escribir después de redimensionar volvía a
   * encoger la caja al tamaño del texto y se perdía el ancho elegido.
   */
  autoWidth: boolean;
}

/**
 * Una nota breve anclada a un punto del canvas.
 *
 * `pinned` deja la burbuja siempre abierta; si es false aparece al pasar por
 * el círculo. `bodyAutoScale` compensa el zoom sólo en la burbuja para que su
 * texto conserve tamaño de pantalla; el Anchor siempre usa escala de mundo.
 */
export interface CommentData {
  text: string;
  pinned: boolean;
  bodyAutoScale: boolean;
}

interface LegacyComment {
  text?: string;
  pinned?: boolean;
  bodyAutoScale?: boolean;
}

export function normalizeComment(raw: LegacyComment | undefined, text = ""): CommentData {
  return {
    text: raw?.text ?? text,
    pinned: raw?.pinned === true,
    bodyAutoScale: raw?.bodyAutoScale === true,
  };
}

/**
 * One freehand pencil stroke, owned by an `ink` card. Points are a flattened
 * [x0, y0, x1, y1, …] polyline NORMALIZED to the card rect (0..1 on each
 * axis), so moving/resizing the card moves/scales the drawing like an image.
 */
export interface InkData {
  /** CSS hex color, e.g. "#111111". */
  color: string;
  /** Stroke width in world px at the card's natural size. */
  size: number;
  points: number[];
  /** Card size (world px) when the stroke was drawn; the rendered stroke
   *  width scales by the ratio of the current size to this one. */
  naturalWidth: number;
  naturalHeight: number;
}

export interface Card {
  id: string;
  type: CardType;
  /** Position in WORLD coordinates (top-left corner). */
  x: number;
  y: number;
  width: number;
  height: number;
  /** Body markdown de un nodo `file`: un caché en memoria del documento que
   *  referencia (ver `file`). Los nodos `text` y `shape` NO lo usan. */
  markdown: string;
  /** Vault-relative path of the embedded document (only for `file` nodes). */
  file?: string;
  /** Pencil stroke data (only for `ink` nodes). Never mutated in place — a
   *  card resize just rescales it at render time via the normalized points. */
  ink?: InkData;
  /** Geometría, relleno y texto (sólo para nodos `shape`). Una forma NO usa
   *  `markdown`: su texto vive en `shape.text`. */
  shape?: ShapeData;
  /** Texto, alineación y fondo (sólo para nodos `text`). Como la forma, un nodo
   *  de texto NO usa `markdown`: su texto vive en `text.text`. */
  text?: TextData;
  /** Mensaje flotante (sólo para nodos `comment`). */
  comment?: CommentData;
  /** IDs of child cards (only for `group` nodes). */
  childrenIds?: string[];
  /** Etiqueta visible sobre el recuadro (sólo para nodos `group`), estilo
   *  Figma. Se asigna sola ("grupo-1", "grupo-2", …) al crear el grupo. */
  name?: string;
  /** Visual color (only for `group` nodes). */
  color?: string;
  /**
   * Cómo se comporta el título del contenedor frente al zoom (sólo para nodos
   * `group`). Ausente o `false` = escala de mundo: el nombre es parte del
   * recuadro y crece y se achica con él, así que su tamaño relativo al
   * contenedor nunca cambia. `true` compensa el zoom y le devuelve el tamaño
   * de pantalla, que es lo que hacía siempre.
   *
   * Es la misma pareja de estados que `CommentData.bodyAutoScale`, y por el
   * mismo motivo: el ancla vive en el mundo y el texto puede querer no seguirlo.
   */
  titleAutoScale?: boolean;
  /** Timestamp used to invalidate the cached texture in the render layer. */
  updatedAt: number;
}

// ---- edges ----------------------------------------------------------------

export type EdgeSide = "top" | "right" | "bottom" | "left";
export type EdgeEnd = "none" | "arrow";
export type EdgeRoute = "elbow" | "straight" | "curved";

/** Valores por defecto de una arista. Se omiten al serializar. */
export const DEFAULT_FROM_END: EdgeEnd = "none";
export const DEFAULT_TO_END: EdgeEnd = "arrow";
export const DEFAULT_ROUTE: EdgeRoute = "elbow";
/** Con el que se dibuja una arista sin color propio (ver `render/EdgeLayer`). */
export const DEFAULT_EDGE_COLOR = "#343331";

/**
 * Un conector entre dos nodos.
 *
 * La arista **no guarda geometría**: sólo dice qué une y, opcionalmente, por
 * qué lado. La ruta se calcula en la capa de render a partir de los rects de
 * sus dos extremos, y de ahí sale gratis que la flecha siga a los nodos al
 * moverlos, redimensionarlos o arrastrarlos dentro de un contenedor. Guardar
 * puntos acá rompería justamente eso.
 *
 * `fromSide`/`toSide` ausentes significan "lado automático": el render elige
 * el par de lados según la posición relativa de los nodos y lo recalcula solos
 * al moverlos. Con un lado explícito, ese extremo queda clavado ahí.
 */
export interface Edge {
  id: string;
  /** Id del nodo donde empieza. */
  fromNode: string;
  /** Id del nodo donde termina. */
  toNode: string;
  fromSide?: EdgeSide;
  toSide?: EdgeSide;
  /** Punta en el origen. Ausente = `DEFAULT_FROM_END`. */
  fromEnd?: EdgeEnd;
  /** Punta en el destino. Ausente = `DEFAULT_TO_END`. */
  toEnd?: EdgeEnd;
  /** Color CSS hex, e.g. "#343331". Ausente = el color por defecto del render. */
  color?: string;
  /** Etiqueta sobre la línea. */
  label?: string;
  /** Forma de la ruta. Ausente = `DEFAULT_ROUTE`. */
  route?: EdgeRoute;
  /** Igual que en `Card`: invalida lo cacheado en la capa de render. */
  updatedAt: number;
}

// ---- vault file tree ------------------------------------------------------

export type VaultFileKind = "md" | "canvas" | "pdf";

export interface VaultFile {
  /** Vault-relative path, e.g. "notas/idea.md". */
  path: string;
  /** Display name (basename), e.g. "idea.md". */
  name: string;
  kind: VaultFileKind;
}

export interface VaultTreeNode {
  /** Vault-relative path. "" for the root. */
  path: string;
  name: string;
  /** Present for directories; absent for files. */
  children?: VaultTreeNode[];
  /** Present for files. */
  kind?: VaultFileKind;
}

export interface Camera {
  x: number;
  y: number;
  zoom: number;
}

export interface CanvasDoc {
  id: string;
  cards: Card[];
  /** Conectores entre nodos. Hermanos de `cards`: una arista es del documento,
   *  no de ninguno de sus dos extremos. */
  edges: Edge[];
  camera: Camera;
}

export function createId(prefix = "card"): string {
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Siguiente nombre libre para un grupo nuevo: "grupo-N", donde N es uno más
 * que el mayor sufijo ya usado. Así renombrar o borrar grupos nunca produce
 * dos "grupo-3" a la vez.
 */
export function nextGroupName(cards: Card[]): string {
  let max = 0;
  for (const c of cards) {
    if (c.type !== "group" || !c.name) continue;
    const m = /^grupo-(\d+)$/.exec(c.name);
    if (m) max = Math.max(max, Number(m[1]));
  }
  return `grupo-${max + 1}`;
}

export function emptyDoc(id = "default"): CanvasDoc {
  return {
    id,
    cards: [],
    edges: [],
    camera: { x: 0, y: 0, zoom: 1 },
  };
}

/**
 * Cómo se guardaba una forma antes de que el texto y el relleno fueran suyos:
 * el texto en `Card.markdown`, un `kind` que ya no existe, y el color elegido
 * puesto en el BORDE mientras el relleno era un tinte casi blanco de ese color.
 */
interface LegacyShape {
  kind?: string;
  fill?: string;
  stroke?: string;
  text?: string;
  align?: string;
}

/**
 * Traduce una forma vieja a la de hoy (ver `LegacyShape`).
 *
 * El relleno sale del borde y no del relleno viejo a propósito: el borde era el
 * color que la persona eligió de la paleta; el relleno era un tinte al 14% de
 * ese color, que nadie eligió. Migrar el borde conserva la intención.
 */
export function normalizeShape(raw: LegacyShape | undefined, markdown: string): ShapeData {
  const kind: ShapeKind =
    raw?.kind === "ellipse" || raw?.kind === "diamond" || raw?.kind === "pill"
      ? raw.kind
      : "rect"; // incluye el viejo "roundRect", que se ve igual que un rect
  return {
    kind,
    fill: raw?.stroke ?? raw?.fill ?? DEFAULT_SHAPE_FILL,
    text: raw?.text ?? markdown ?? "",
    // Centrado es como se dibujó SIEMPRE el texto de una figura: una forma
    // guardada antes de que la alineación se pudiera elegir tiene que seguir
    // viéndose igual al abrirla.
    align:
      typeof raw?.align === "string" && ALIGNS.has(raw.align) ? (raw.align as TextAlign) : "center",
  };
}

/** Cómo se guardaba un nodo de texto antes de tener estilo propio: el texto
 *  plano en `Card.markdown` y nada más. */
interface LegacyText {
  align?: string;
  background?: string;
  color?: string;
  autoWidth?: boolean;
}

const ALIGNS = new Set<string>(["left", "center", "right"]);

/**
 * Completa el estilo de un nodo de texto, viniendo de donde venga.
 *
 * Un documento escrito antes de esta versión no trae ninguno de los tres
 * campos, y su texto está en `markdown`: eso es exactamente lo que se migra
 * acá. Los valores por defecto son los de un texto recién creado —sin fondo,
 * alineado a la izquierda— salvo `autoWidth`, que arranca en `false` para lo
 * que ya existía: esos nodos tienen un ancho que alguien vio, y encogerlos al
 * abrir el archivo sería cambiarle el documento por sorpresa.
 */
export function normalizeText(raw: LegacyText | undefined, text: string): TextData {
  return {
    text,
    align:
      raw && typeof raw.align === "string" && ALIGNS.has(raw.align)
        ? (raw.align as TextAlign)
        : "left",
    background: typeof raw?.background === "string" ? raw.background : TEXT_BG_NONE,
    color: typeof raw?.color === "string" ? raw.color : TEXT_COLOR_DEFAULT,
    autoWidth: raw?.autoWidth === true,
  };
}

/**
 * Completa los campos que un documento viejo puede no tener, para que el resto
 * del código no tenga que preguntar. Un `CanvasDoc` guardado antes de que
 * existieran las aristas no trae `edges`, y sin esto cualquier `doc.edges.map`
 * explota. Se aplica en la única puerta de entrada al store (`createDocStore` y
 * `loadDoc`), así que alcanza con eso.
 *
 * Lo mismo vale para las formas guardadas en IndexedDB antes de que el texto
 * fuera suyo: acá se traslada. Un `.canvas` en disco no pasa por esta función
 * —lo migra `fromJSONCanvas`—, pero el borrador local sí.
 */
export function normalizeDoc(doc: CanvasDoc): CanvasDoc {
  const edges = doc.edges ?? [];
  // `text.color` entra en la cuenta de lo viejo aunque `text` ya exista: un
  // borrador guardado antes de que el color se pudiera elegir trae el resto del
  // estilo y no trae ése, y un nodo sin color se dibuja sin color.
  const stale = doc.cards.some(
    (c) =>
      (c.type === "shape" && (c.shape?.text === undefined || c.shape.align === undefined)) ||
      (c.type === "text" && (c.text === undefined || c.text.color === undefined)) ||
      (c.type === "comment" &&
        (c.comment === undefined ||
          c.comment.pinned === undefined ||
          c.comment.bodyAutoScale === undefined)),
  );
  if (edges === doc.edges && !stale) return doc;
  const cards = stale
    ? doc.cards.map((c) => {
        if (c.type === "shape" && c.shape?.text === undefined) {
          return { ...c, markdown: "", shape: normalizeShape(c.shape, c.markdown) };
        }
        // Ya tiene texto propio, pero de antes de que la alineación se pudiera
        // elegir: se completa sin tocar lo demás (queda centrada, como se veía).
        if (c.type === "shape" && c.shape !== undefined && c.shape.align === undefined) {
          return { ...c, shape: normalizeShape(c.shape, c.shape.text) };
        }
        // Lo mismo para el texto suelto: hasta esta versión vivía en `markdown`.
        if (c.type === "text" && c.text === undefined) {
          return { ...c, markdown: "", text: normalizeText(undefined, c.markdown) };
        }
        // Ya tiene estilo, pero de antes de que hubiera color: se completa sin
        // tocar lo demás.
        if (c.type === "text" && c.text !== undefined && c.text.color === undefined) {
          return { ...c, text: normalizeText(c.text, c.text.text) };
        }
        if (
          c.type === "comment" &&
          (c.comment === undefined ||
            c.comment.pinned === undefined ||
            c.comment.bodyAutoScale === undefined)
        ) {
          return { ...c, markdown: "", comment: normalizeComment(c.comment, c.markdown) };
        }
        return c;
      })
    : doc.cards;
  return { ...doc, cards, edges };
}
